import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, InputAdornment, Pagination, TextField, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { ChangeEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import useApi from '../../../hooks/api';
import useIsMobile from '../../../hooks/isMobile';
import useProfile, { type ExtendedProfile } from '../../../hooks/profile';
import UniformGalleryImageCard from './UniformGalleryImageCard';
import UniformGalleryImageModal from './UniformGalleryImageModal';
import { fitUniformGalleryGrid } from './uniformGalleryGridLayout';
import {
    type ParsedUniformImageName,
    fileNameFromImageUrl,
    formatUniformCaptureUnixForDisplay,
    parseUniformImageFileName,
} from './uniformImageFileName';
import { formatUniformPrefixForDisplay } from './uniformPrefixDisplay';

function decodePrefixParam(encoded: string): string {
    try {
        return decodeURIComponent(encoded);
    } catch {
        return encoded;
    }
}

function uniformFolderImageSearchHaystack(
    fileName: string,
    parsed: ParsedUniformImageName,
    profile: ExtendedProfile,
): string {
    const parts: string[] = [fileName];
    if (parsed.kind === 'structured') {
        parts.push(
            parsed.plate,
            parsed.driverName,
            parsed.internalId,
            String(parsed.capturedUnix),
            formatUniformCaptureUnixForDisplay(parsed.capturedUnix, profile),
        );
    }
    return parts.join(' ').toLowerCase();
}

/** Target cell size for gallery layout and pagination (px). */
const GALLERY_CELL_WIDTH = 240;
const GALLERY_CELL_HEIGHT = 240;
/** Must match grid `gap` (MUI spacing 2 → 16px). */
const GALLERY_GRID_GAP_PX = 16;
/** Folder listings are stable; avoid refetching (focus, remount, etc.) for this window. */
const UNIFORM_FOLDER_URLS_STALE_MS = 30 * 60 * 1000;

const UniformFolderImages: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { profile } = useProfile();
    const isMobile = useIsMobile();
    const history = useHistory();
    const { prefix: encodedPrefix } = useParams<{ prefix: string }>();
    const { api } = useApi();

    const since = useMemo(() => decodePrefixParam(encodedPrefix), [encodedPrefix]);

    const sinceDisplay = useMemo(() => formatUniformPrefixForDisplay(since, profile), [since, profile]);

    const galleryViewportRef = useRef<HTMLDivElement>(null);
    const [gridLayout, setGridLayout] = useState({ cols: 2, rows: 2 });

    const pageSize = gridLayout.cols * gridLayout.rows;

    const [page, setPage] = useState(1);
    const [quickSearch, setQuickSearch] = useState('');
    const [modalSrc, setModalSrc] = useState<string | null>(null);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['uniform_wellness_images', 'folder_urls', since],
        queryFn: async () => {
            const res = await api.apiV0CustomerUniformWellnessImagesSinceGet({ since });
            return res.data ?? [];
        },
        enabled: since.length > 0,
        staleTime: UNIFORM_FOLDER_URLS_STALE_MS,
        cacheTime: UNIFORM_FOLDER_URLS_STALE_MS,
    });

    const filteredData = useMemo(() => {
        if (!data?.length) {
            return [];
        }
        const q = quickSearch.trim().toLowerCase();
        if (!q) {
            return data;
        }
        return data.filter((src) => {
            const name = fileNameFromImageUrl(src);
            const parsed = parseUniformImageFileName(name);
            return uniformFolderImageSearchHaystack(name, parsed, profile).includes(q);
        });
    }, [data, quickSearch, profile]);

    const fileCount = filteredData.length;
    const totalPages = Math.max(1, Math.ceil(fileCount / pageSize));
    const rawFileCount = data?.length ?? 0;
    const galleryOpen = !isError && !isLoading && fileCount > 0;

    useLayoutEffect(() => {
        if (!galleryOpen) {
            return undefined;
        }
        const el = galleryViewportRef.current;
        if (!el) {
            return undefined;
        }
        const measure = () => {
            const { clientWidth, clientHeight } = el;
            const next = fitUniformGalleryGrid(
                clientWidth,
                clientHeight,
                GALLERY_CELL_WIDTH,
                GALLERY_CELL_HEIGHT,
                GALLERY_GRID_GAP_PX,
            );
            setGridLayout((prev) => (prev.cols === next.cols && prev.rows === next.rows ? prev : next));
        };
        measure();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [galleryOpen]);

    useEffect(() => {
        setPage(1);
    }, [since, quickSearch]);

    useEffect(() => {
        setPage((p) => Math.min(p, totalPages));
    }, [totalPages]);

    const prevGridLayoutRef = useRef(gridLayout);
    useEffect(() => {
        const prev = prevGridLayoutRef.current;
        const gridDimsChanged = prev.cols !== gridLayout.cols || prev.rows !== gridLayout.rows;
        prevGridLayoutRef.current = gridLayout;
        if (!gridDimsChanged) {
            return;
        }
        if (modalSrc != null) {
            const idx = filteredData.indexOf(modalSrc);
            if (idx >= 0 && pageSize > 0) {
                setPage(Math.floor(idx / pageSize) + 1);
            }
        } else {
            setPage(1);
        }
    }, [gridLayout, modalSrc, filteredData, pageSize]);

    useEffect(() => {
        if (modalSrc != null && !filteredData.includes(modalSrc)) {
            setModalSrc(null);
        }
    }, [filteredData, modalSrc]);

    const openFolderImageModal = useCallback(
        (src: string) => {
            setModalSrc(src);
            const idx = filteredData.indexOf(src);
            if (idx >= 0 && pageSize > 0) {
                setPage(Math.floor(idx / pageSize) + 1);
            }
        },
        [filteredData, pageSize],
    );

    const closeFolderImageModal = useCallback(() => {
        setModalSrc(null);
    }, []);

    const navigateFolderImageModal = useCallback(
        (nextSrc: string) => {
            setModalSrc(nextSrc);
            const idx = filteredData.indexOf(nextSrc);
            if (idx >= 0 && pageSize > 0) {
                setPage(Math.floor(idx / pageSize) + 1);
            }
        },
        [filteredData, pageSize],
    );

    const pageItems = useMemo(() => {
        if (!filteredData.length) {
            return [];
        }
        const start = (page - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, page, pageSize]);

    return (
        <Box
            sx={{
                p: 2,
                height: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    columnGap: 2,
                    rowGap: 1,
                }}
            >
                <Button
                    id="uniform-images-back"
                    data-testid="uniform-images-back"
                    startIcon={
                        <ArrowBackIcon sx={theme.direction === 'rtl' ? { transform: 'scaleX(-1)' } : undefined} />
                    }
                    onClick={() => history.push('/uniform_images')}
                    sx={{ flexShrink: 0 }}
                >
                    {t('content.uniform.back')}
                </Button>
                <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    component="div"
                    title={since}
                    sx={{
                        flex: '1 1 140px',
                        minWidth: 0,
                        wordBreak: 'break-word',
                    }}
                >
                    {sinceDisplay}
                </Typography>
                <Box
                    sx={{
                        flex: isMobile ? '1 1 100%' : '1 1 200px',
                        maxWidth: isMobile ? '100%' : 400,
                        minWidth: 0,
                    }}
                >
                    <TextField
                        id="uniform-images-folder-quick-search"
                        data-testid="uniform-images-folder-quick-search"
                        variant="standard"
                        size="small"
                        fullWidth
                        placeholder={t('table.search')}
                        value={quickSearch}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setQuickSearch(e.target.value)}
                        disabled={isLoading || isError || rawFileCount === 0}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" aria-hidden />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Box>
            {isError ? (
                <Typography variant="body2" color="error">
                    {t('content.uniform.load_error')}
                </Typography>
            ) : null}
            {!isError && !isLoading && rawFileCount === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t('content.uniform.placeholder')}
                </Typography>
            ) : null}
            {!isError && !isLoading && rawFileCount > 0 && fileCount === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t('content.uniform.search_no_results')}
                </Typography>
            ) : null}
            {!isError && !isLoading && fileCount > 0 ? (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    {modalSrc != null ? (
                        <UniformGalleryImageModal
                            src={modalSrc}
                            open
                            orderedUrls={filteredData}
                            onClose={closeFolderImageModal}
                            onNavigate={navigateFolderImageModal}
                        />
                    ) : null}
                    <Box
                        ref={galleryViewportRef}
                        data-testid="uniform-images-gallery"
                        sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}
                    >
                        <Box
                            sx={{
                                minHeight: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${gridLayout.cols}, minmax(0, ${GALLERY_CELL_WIDTH}px))`,
                                    gap: `${GALLERY_GRID_GAP_PX}px`,
                                    gridAutoRows: `${GALLERY_CELL_HEIGHT}px`,
                                    justifyContent: 'center',
                                    alignContent: 'center',
                                }}
                            >
                                {pageItems.map((src) => (
                                    <UniformGalleryImageCard
                                        key={src}
                                        src={src}
                                        cellWidth={GALLERY_CELL_WIDTH}
                                        cellHeight={GALLERY_CELL_HEIGHT}
                                        onOpenModal={() => openFolderImageModal(src)}
                                        isModalActive={modalSrc === src}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                    {totalPages > 1 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0, py: 1 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
};

export default UniformFolderImages;
